import React, { useEffect, useState, Children } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ChevronDown, MapPin, Clock, Phone, Instagram } from 'lucide-react';
export function NatuCafePage() {
  const {
    scrollY
  } = useScroll();
  const heroY = useTransform(scrollY, [0, 1000], [0, 300]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0]);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const fadeUpVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  return <div className="bg-[#0B0B0B] min-h-screen text-[#F5F0EB] font-sans selection:bg-[#C6A969] selection:text-[#0B0B0B] overflow-x-hidden relative" data-id="element-5045">
      {/* Film Grain Overlay */}
      <div className="pointer-events-none fixed inset-0 z-50 opacity-[0.03]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} data-id="element-5046" />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-700 ${isScrolled ? 'bg-[#0B0B0B]/90 backdrop-blur-md py-4' : 'bg-transparent py-8'}`} data-id="element-5047">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-5048">
          <div className="font-serif text-2xl md:text-3xl tracking-wider font-medium text-[#F5F0EB]" data-id="element-5049">
            NatuCafé
          </div>
          <div className="hidden md:flex items-center gap-10 text-xs tracking-[0.2em] uppercase text-[#F5F0EB]/70" data-id="element-5050">
            <button onClick={() => scrollTo('origen')} className="hover:text-[#C6A969] transition-colors" data-id="element-5051">
              Origen
            </button>
            <button onClick={() => scrollTo('menu')} className="hover:text-[#C6A969] transition-colors" data-id="element-5052">
              Menú
            </button>
            <button onClick={() => scrollTo('experiencia')} className="hover:text-[#C6A969] transition-colors" data-id="element-5053">
              Experiencia
            </button>
            <button onClick={() => scrollTo('contacto')} className="hover:text-[#C6A969] transition-colors" data-id="element-5054">
              Contacto
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden flex items-center justify-center" data-id="element-5055">
        <motion.div style={{
        y: heroY,
        opacity: heroOpacity
      }} className="absolute inset-0 w-full h-full" data-id="element-5056">
          <div className="absolute inset-0 bg-black/60 z-10" data-id="element-5057" />
          <img src="https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=1920&q=80" alt="Premium Coffee" className="w-full h-full object-cover" data-id="element-5058" />
        </motion.div>

        <div className="relative z-20 max-w-5xl mx-auto px-6 text-center mt-20" data-id="element-5059">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-5060">
            <motion.h1 variants={fadeUpVariants} className="font-serif text-5xl md:text-7xl lg:text-8xl text-[#F5F0EB] leading-[1.1] tracking-wide" data-id="element-5061">
              Donde el café <br className="hidden md:block" data-id="element-5062" />
              se convierte en arte
            </motion.h1>
            <motion.p variants={fadeUpVariants} className="font-sans font-light text-lg md:text-xl text-[#F5F0EB]/80 max-w-2xl mx-auto leading-relaxed tracking-wide" data-id="element-5063">
              Cultivado a 1,700 metros en Los Santos, Costa Rica.{' '}
              <br className="hidden md:block" data-id="element-5064" />
              Tostado con maestría. Servido con devoción.
            </motion.p>
            <motion.div variants={fadeUpVariants} className="pt-8" data-id="element-5065">
              <button onClick={() => scrollTo('menu')} className="px-10 py-4 border border-[#C6A969] text-[#C6A969] text-xs uppercase tracking-[0.2em] hover:bg-[#C6A969] hover:text-[#0B0B0B] transition-all duration-500" data-id="element-5066">
                Descubrir la experiencia
              </button>
            </motion.div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 2,
        duration: 1
      }} className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20" data-id="element-5067">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          repeat: Infinity,
          duration: 2,
          ease: 'easeInOut'
        }} data-id="element-5068">
            <ChevronDown className="w-6 h-6 text-[#F5F0EB]/40" data-id="element-5069" />
          </motion.div>
        </motion.div>
      </section>

      {/* Origin Section */}
      <section id="origen" className="py-32 md:py-48 relative" data-id="element-5070">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-5071">
          <div className="w-full h-px bg-[#C6A969]/20 mb-32" data-id="element-5072" />

          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-center" data-id="element-5073">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="order-2 md:order-1" data-id="element-5074">
              <motion.h2 variants={fadeUpVariants} className="font-serif text-4xl md:text-5xl lg:text-6xl mb-8 leading-tight" data-id="element-5075">
                Nacido en las montañas <br data-id="element-5076" /> de Los Santos
              </motion.h2>
              <motion.div variants={fadeUpVariants} className="space-y-6 font-sans font-light text-[#F5F0EB]/70 leading-[1.9] text-lg" data-id="element-5077">
                <p data-id="element-5078">
                  A 1,700 metros sobre el nivel del mar, donde la neblina abraza
                  las laderas y el suelo volcánico nutre cada raíz, nace nuestro
                  café. Un entorno privilegiado que confiere a cada grano una
                  acidez brillante y notas complejas.
                </p>
                <p data-id="element-5079">
                  NatuCafé no es solo una cafetería; es el tributo a
                  generaciones de caficultores que han perfeccionado el arte del
                  cultivo. Cada taza que servimos es el resultado de un proceso
                  meticuloso, desde la recolección manual hasta el tueste
                  artesanal.
                </p>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 1.2,
            ease: [0.25, 0.1, 0.25, 1]
          }} className="order-1 md:order-2 aspect-[3/4] md:aspect-[4/5] overflow-hidden" data-id="element-5080">
              <img src="https://images.unsplash.com/photo-1611854779393-1b2da9d400fe?w=800&q=80" alt="Coffee Plantation" className="w-full h-full object-cover" data-id="element-5081" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-32 md:py-48 bg-[#111111]" data-id="element-5082">
        <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-5083">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center mb-24" data-id="element-5084">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6" data-id="element-5085">
              Nuestra Carta
            </h2>
            <p className="font-sans font-light text-[#F5F0EB]/60 tracking-widest uppercase text-xs" data-id="element-5086">
              Una selección curada para paladares exigentes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-x-24 gap-y-20" data-id="element-5087">
            {/* Category 1 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-5088">
              <motion.h3 variants={fadeUpVariants} className="text-xs font-sans uppercase tracking-[0.3em] text-[#C6A969] mb-8 pb-4 border-b border-[#C6A969]/20" data-id="element-5089">
                Café de Origen
              </motion.h3>
              <div className="space-y-8" data-id="element-5090">
                {[{
                name: 'Espresso Clásico',
                price: '₡1,800',
                desc: 'Intenso, notas a cacao oscuro y nuez tostada.'
              }, {
                name: 'Americano',
                price: '₡2,000',
                desc: 'Equilibrado, cuerpo medio y final limpio.'
              }, {
                name: 'Cappuccino',
                price: '₡2,500',
                desc: 'Espresso perfecto con leche texturizada sedosa.'
              }, {
                name: 'Latte',
                price: '₡2,500',
                desc: 'Suave y cremoso, ideal para resaltar el dulzor natural.'
              }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="group" data-id="element-5091">
                    <div className="flex justify-between items-baseline mb-2" data-id="element-5092">
                      <h4 className="font-serif text-xl text-[#F5F0EB]" data-id="element-5093">
                        {item.name}
                      </h4>
                      <span className="font-sans font-light text-[#F5F0EB]/80 text-sm" data-id="element-5094">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed" data-id="element-5095">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Category 2 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-5096">
              <motion.h3 variants={fadeUpVariants} className="text-xs font-sans uppercase tracking-[0.3em] text-[#C6A969] mb-8 pb-4 border-b border-[#C6A969]/20" data-id="element-5097">
                Métodos de Preparación
              </motion.h3>
              <div className="space-y-8" data-id="element-5098">
                {[{
                name: 'Pour Over V60',
                price: '₡3,200',
                desc: 'Resalta la acidez brillante y notas florales.'
              }, {
                name: 'Chemex',
                price: '₡3,500',
                desc: 'Extracción limpia, cuerpo ligero y elegante.'
              }, {
                name: 'Aeropress',
                price: '₡3,000',
                desc: 'Cuerpo denso, sabores intensos y concentrados.'
              }, {
                name: 'Cold Brew',
                price: '₡3,200',
                desc: 'Maceración de 18 horas, refrescante y sin amargor.'
              }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="group" data-id="element-5099">
                    <div className="flex justify-between items-baseline mb-2" data-id="element-5100">
                      <h4 className="font-serif text-xl text-[#F5F0EB]" data-id="element-5101">
                        {item.name}
                      </h4>
                      <span className="font-sans font-light text-[#F5F0EB]/80 text-sm" data-id="element-5102">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed" data-id="element-5103">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Category 3 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-5104">
              <motion.h3 variants={fadeUpVariants} className="text-xs font-sans uppercase tracking-[0.3em] text-[#C6A969] mb-8 pb-4 border-b border-[#C6A969]/20" data-id="element-5105">
                Especialidades de la Casa
              </motion.h3>
              <div className="space-y-8" data-id="element-5106">
                {[{
                name: 'Café de la Casa',
                price: '₡3,800',
                desc: 'Nuestra receta secreta. Una experiencia sensorial única.',
                highlight: true
              }, {
                name: 'Affogato',
                price: '₡3,500',
                desc: 'Espresso caliente sobre helado artesanal de vainilla.'
              }, {
                name: 'Mocha Artesanal',
                price: '₡3,200',
                desc: 'Espresso, leche texturizada y cacao puro al 70%.'
              }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="group" data-id="element-5107">
                    <div className="flex justify-between items-baseline mb-2" data-id="element-5108">
                      <h4 className={`font-serif text-xl ${item.highlight ? 'text-[#C6A969]' : 'text-[#F5F0EB]'}`} data-id="element-5109">
                        {item.name}
                      </h4>
                      <span className={`font-sans font-light text-sm ${item.highlight ? 'text-[#C6A969]' : 'text-[#F5F0EB]/80'}`} data-id="element-5110">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed" data-id="element-5111">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Category 4 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-5112">
              <motion.h3 variants={fadeUpVariants} className="text-xs font-sans uppercase tracking-[0.3em] text-[#C6A969] mb-8 pb-4 border-b border-[#C6A969]/20" data-id="element-5113">
                Repostería Artesanal
              </motion.h3>
              <div className="space-y-8" data-id="element-5114">
                {[{
                name: 'Croissant de Almendra',
                price: '₡2,800',
                desc: 'Horneado diariamente, relleno de crema de almendras.'
              }, {
                name: 'Tarta de Chocolate',
                price: '₡3,500',
                desc: 'Chocolate oscuro belga, textura sedosa y base crujiente.'
              }, {
                name: 'Cheesecake',
                price: '₡3,200',
                desc: 'Estilo neoyorquino, con coulis de frutos rojos de la zona.'
              }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="group" data-id="element-5115">
                    <div className="flex justify-between items-baseline mb-2" data-id="element-5116">
                      <h4 className="font-serif text-xl text-[#F5F0EB]" data-id="element-5117">
                        {item.name}
                      </h4>
                      <span className="font-sans font-light text-[#F5F0EB]/80 text-sm" data-id="element-5118">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed" data-id="element-5119">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experience / Ambience Section */}
      <section id="experiencia" className="relative py-48 md:py-64 overflow-hidden" data-id="element-5120">
        <div className="absolute inset-0 w-full h-full" data-id="element-5121">
          <div className="absolute inset-0 bg-[#0B0B0B]/70 z-10" data-id="element-5122" />
          <img src="https://images.unsplash.com/photo-1559305616-3f99cd43e353?w=1920&q=80" alt="NatuCafé Ambience" className="w-full h-full object-cover object-center" data-id="element-5123" />
        </div>

        <div className="relative z-20 max-w-4xl mx-auto px-6 text-center" data-id="element-5124">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} data-id="element-5125">
            <motion.h2 variants={fadeUpVariants} className="font-serif text-4xl md:text-5xl lg:text-6xl mb-10 leading-tight" data-id="element-5126">
              "Un espacio para conversaciones que importan"
            </motion.h2>
            <motion.p variants={fadeUpVariants} className="font-sans font-light text-lg md:text-xl text-[#F5F0EB]/80 leading-relaxed" data-id="element-5127">
              Diseñado para la pausa consciente. Sin distracciones, sin prisa.{' '}
              <br className="hidden md:block" data-id="element-5128" />
              Solo usted, una excelente taza de café y el momento presente.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Craftsmanship Section */}
      <section className="py-32 md:py-48" data-id="element-5129">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-5130">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-16 text-center" data-id="element-5131">
            <motion.div variants={fadeUpVariants} className="space-y-6" data-id="element-5132">
              <div className="font-serif text-5xl text-[#C6A969]" data-id="element-5133">1,700m</div>
              <h4 className="font-sans uppercase tracking-[0.2em] text-sm text-[#F5F0EB]" data-id="element-5134">
                Altitud Perfecta
              </h4>
              <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed max-w-xs mx-auto" data-id="element-5135">
                Cultivado en las alturas de Los Santos, permitiendo una
                maduración lenta que concentra los azúcares naturales.
              </p>
            </motion.div>

            <motion.div variants={fadeUpVariants} className="space-y-6" data-id="element-5136">
              <div className="font-serif text-5xl text-[#C6A969]" data-id="element-5137">+30</div>
              <h4 className="font-sans uppercase tracking-[0.2em] text-sm text-[#F5F0EB]" data-id="element-5138">
                Años de Tradición
              </h4>
              <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed max-w-xs mx-auto" data-id="element-5139">
                El conocimiento transmitido de generación en generación,
                perfeccionando cada etapa del proceso.
              </p>
            </motion.div>

            <motion.div variants={fadeUpVariants} className="space-y-6" data-id="element-5140">
              <div className="font-serif text-5xl text-[#C6A969]" data-id="element-5141">Arte</div>
              <h4 className="font-sans uppercase tracking-[0.2em] text-sm text-[#F5F0EB]" data-id="element-5142">
                Tostado Artesanal
              </h4>
              <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed max-w-xs mx-auto" data-id="element-5143">
                Tostamos en pequeños lotes para garantizar que cada grano
                alcance su máximo potencial de sabor.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" className="bg-[#0B0B0B] pt-20 pb-10 border-t border-[#C6A969]/20" data-id="element-5144">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-5145">
          <div className="grid md:grid-cols-3 gap-16 mb-24" data-id="element-5146">
            <div data-id="element-5147">
              <h2 className="font-serif text-3xl mb-6" data-id="element-5148">NatuCafé</h2>
              <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed max-w-xs" data-id="element-5149">
                La máxima expresión del café de especialidad en Costa Rica.
              </p>
            </div>

            <div className="space-y-6" data-id="element-5150">
              <h4 className="font-sans uppercase tracking-[0.2em] text-xs text-[#C6A969]" data-id="element-5151">
                Visítenos
              </h4>
              <div className="space-y-4 font-sans font-light text-[#F5F0EB]/70 text-sm" data-id="element-5152">
                <p className="flex items-start gap-3" data-id="element-5153">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#C6A969]" data-id="element-5154" />
                  <span data-id="element-5155">
                    Los Santos, San José
                    <br data-id="element-5156" />
                    Costa Rica
                  </span>
                </p>
                <p className="flex items-start gap-3" data-id="element-5157">
                  <Clock className="w-4 h-4 mt-0.5 text-[#C6A969]" data-id="element-5158" />
                  <span data-id="element-5159">
                    Lunes a Sábado
                    <br data-id="element-5160" />
                    7:00 am - 6:00 pm
                  </span>
                </p>
              </div>
            </div>

            <div className="space-y-6" data-id="element-5161">
              <h4 className="font-sans uppercase tracking-[0.2em] text-xs text-[#C6A969]" data-id="element-5162">
                Contacto
              </h4>
              <div className="space-y-4 font-sans font-light text-[#F5F0EB]/70 text-sm" data-id="element-5163">
                <p className="flex items-center gap-3" data-id="element-5164">
                  <Phone className="w-4 h-4 text-[#C6A969]" data-id="element-5165" />
                  <span data-id="element-5166">+506 8888-8888</span>
                </p>
                <p className="flex items-center gap-3" data-id="element-5167">
                  <Instagram className="w-4 h-4 text-[#C6A969]" data-id="element-5168" />
                  <span data-id="element-5169">@natucafe.cr</span>
                </p>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-5170">
            <p className="font-sans font-light text-[#F5F0EB]/30 text-xs" data-id="element-5171">
              © 2026 NatuCafé. Todos los derechos reservados.
            </p>
            <div className="font-serif text-[#F5F0EB]/20 text-xl" data-id="element-5172">N</div>
          </div>
        </div>
      </footer>
    </div>;
}